import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/auth'
import { useSeo } from '@/lib/useSeo'
import { AuthLayout, inputBase, labelBase, primaryButton } from '@/components/public/AuthLayout'

type LocationState = { from?: string }

export function SignIn() {
  useSeo({ title: 'Sign in', description: 'Sign in to VICE.' })
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error: err } = await signIn(email, password)
    setSubmitting(false)
    if (err) {
      setError(err.message)
      return
    }
    const from = (location.state as LocationState | null)?.from ?? '/dashboard'
    navigate(from, { replace: true })
  }

  return (
    <AuthLayout
      title="VICE Sign In"
      subtitle="Accounts are provisioned by invitation. Contact us if you need access."
      footerSlot={
        <>
          Need access?{' '}
          <Link to="/contact" className="text-secondary font-semibold hover:text-primary underline decoration-accent-turquoise underline-offset-4">
            Contact us
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" aria-labelledby="signin-title">
        <h2 id="signin-title" className="sr-only">Sign in form</h2>
        <div>
          <label htmlFor="email" className={labelBase}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputBase}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="password" className={labelBase + ' mb-0'}>
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs text-secondary hover:text-primary font-semibold"
            >
              Forgot?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputBase}
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-error">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} className={primaryButton}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="text-xs text-neutral-500 leading-relaxed">
          Sessions are logged for security and operational reporting.
        </p>
      </form>
    </AuthLayout>
  )
}
